import React, { useEffect, useMemo } from 'react';
import {
  ColDef,
  DateFilterModule,
  NumberFilterModule,
  PaginationModule,
  QuickFilterModule,
  RowSelectionModule,
  TextFilterModule,
  ValidationModule,
  ExternalFilterModule,
  ModuleRegistry,
  CustomFilterModule
} from 'ag-grid-community';
import { ClientSideRowModelModule } from 'ag-grid-community'; 
import { formatDate, convertDbTimeIntoString } from '../../services/Helper';
import { BadgeStatus } from '../BadgeStatus/BadgeStatus';
import { BadgeEventTitle } from '../BadgeEventTitle/BadgeEventTitle';
import { ProgramOffice, User, EventStatus } from '../../services/Types';
import { Event } from '../../services/Types';
import { Typography } from '@mui/material';
import { ActionMenus } from '../ActionMenus/ActionMenus';
import { useEventGrid } from '../../hooks/useEventGrid';

ModuleRegistry.registerModules([
  PaginationModule,
  RowSelectionModule,
  QuickFilterModule,
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  ExternalFilterModule,
  CustomFilterModule,
  ValidationModule /* Development Only */,
]);

interface EventGridCompactProps {
  searchCriteria: User | ProgramOffice | EventStatus;
  allEvents?: boolean;
}

export const EventGridCompact: React.FC<EventGridCompactProps> = ({ searchCriteria, allEvents = true }) => {
  const gridStyle = useMemo(() => ({ height: "calc(100vh - 473px)", width: "100%" }), []);

  const columnDefs: ColDef<Event>[] = [
    { field: 'id' as keyof Event,
      headerName: 'ID',
      hide: true,
      lockPosition: true,
      cellDataType: "number",
    },
    { field: 'title',
      headerName: 'Event',
      minWidth: 400,
      cellRenderer: (params: any) => (
        <div style={{ display: 'flex', alignItems: 'left', gap: '5px', flexDirection: 'column', paddingTop: '10px' }}>
          <Typography variant="body2" style={{ lineHeight: '1em' }}>
            {params.data.roomBooking?.[0] ? (
              `${formatDate(params.data.roomBooking[0].date, 'date') as string} ${convertDbTimeIntoString(params.data.roomBooking[0].startTime)} - ${convertDbTimeIntoString(params.data.roomBooking[0].endTime)}`
            ) : (
              "Date/Time: TBD"
            )}
          </Typography>
          <BadgeEventTitle eventType={params.data.eventType} eventTitle={params.data.title} />
        </div>
      ),
      flex: 3
    },
    {
      field: 'createrId',
      headerName: 'Requestor ID',
      type: 'number',
      filter: 'agNumberColumnFilter',
      hide: true
    },
    {
      colId: 'programOfficeId',
      headerName: 'Program Office ID',
      type: 'number',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: any) => {
        return params.data?.programOffice ? params.data?.programOffice?.id : '';
      },
      hide: true
    },
    { field: 'status',
      headerName: 'Status',
      filter: 'agTextColumnFilter',
      cellRenderer: (params: any) => <BadgeStatus status={params.data.status} />,
      width: 190,
    },
    {
      headerName: "Actions",
      width: 100,
      cellRenderer: (params: any) => <ActionMenus eventData={params.data} />,
      pinned: 'right',
    }
  ];

  const { gridRef, renderGrid } = useEventGrid({ columnDefs, gridStyle, allEvents });

  // Filter on the searchCriteria value
  useEffect(() => {
    if (typeof searchCriteria === 'string') {
      // Handle EventStatus case
      gridRef?.current?.api?.setFilterModel({
        status: { filterType: 'text', type: 'equals', filter: searchCriteria }
      });
    } else if (searchCriteria?.id !== undefined) {
      // if the searchCriteria is a User
      if ('firstName' in searchCriteria && 'lastName' in searchCriteria) {
        gridRef?.current?.api?.setFilterModel({
          createrId: { filterType: 'number', type: 'equals', filter: searchCriteria.id }
        });
        // if the searchCriteria is a ProgramOffice
      } else if ('title' in searchCriteria && 'acronym' in searchCriteria) {
        gridRef?.current?.api?.setFilterModel({
          programOfficeId: { filterType: 'number', type: 'equals', filter: searchCriteria.id }
        });
      }
    } else if (searchCriteria?.id === undefined) {
      gridRef?.current?.api?.setFilterModel(null);
    }
  }, [searchCriteria]);

  return renderGrid();
}; 